import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators'
import { Action, Store } from '@ngrx/store';

import { from, Observable, of, withLatestFrom } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, takeUntil } from 'rxjs/operators';

import * as discover from '../actions/discover';
import * as fromRoot from '../reducers'
import * as user from '../actions/user';
import * as engage from '../actions/engage';
import * as engagement from '../actions/engagement';
import { DiscoverService } from '../discover/discover.service';
import * as automation from '../actions/automation';
import { DiscoverConstants } from 'spotlight-ui/app/discover/discover.constants';
import { CampaignType } from '../models/engage/campaign-details.model';

@Injectable()
export class DiscoverEffects {
  public loadDiscoverTiles$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(
      engage.ActionTypes.CANCEL_BONUS_POINT_PROMOTION_FROM_CALENDAR,
      engage.ActionTypes.CANCEL_COMMUNITY_CARE_EVENT_FROM_CALENDAR,
      engage.ActionTypes.CANCEL_THIRD_PARTY_PROMOTION_SUCCESS,
      engagement.ActionTypes.CANCEL_THIRD_PARTY_PROMOTION_SUCCESS,
      engagement.ActionTypes.SEND_COMMUNITY_CARE_EVENT_SUCCESS,
      engagement.ActionTypes.SEND_THIRD_PARTY_PROMOTION_SUCCESS,
      engagement.ActionTypes.STOP_AUTOMATION_SUCCESS,
      automation.ActionTypes.STOP_AUTOMATION_COMPLETE,
      engagement.ActionTypes.STOP_COMMUNITY_CARE_EVENT_SUCCESS,
      engagement.ActionTypes.STOP_FREE_IN_CART_SUCCESS,
      user.ActionTypes.GET_LOCATION_DETAILS_SUCCESS
    ),
    withLatestFrom(this.store.select(fromRoot.getAutomationsFeatureEnabledMap)),
    switchMap(([action, featureMap]) => this.ds.loadDiscoverTiles(featureMap).pipe(
      concatLatestFrom(() => this.store.select(fromRoot.getSelectedLocation)),
      map(([res, location]) => new discover.LoadDiscoverTilesSuccessAction(res, location.locationNumber)),
      catchError((error) => of(new discover.LoadDiscoverTilesErrorAction(error)))
    ))
  )});

  public loadFirstTimeCustomers$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(discover.ActionTypes.LOAD_DISCOVER_TILES_SUCCESS),
    map((action: any) => action.payload),
    withLatestFrom(this.store.select(fromRoot.getAutomationsFeatureEnabledMap)),
    filter(([res, featureMap]) =>!featureMap['engage-first'] && this.ds.findById('engage-first', res)),
    switchMap(() => this.ds.loadFirstTimeCustomers().pipe(
      map((res) => new discover.LoadTileDataSuccessAction(res)),
      catchError((error) => of(
        new discover.LoadTileDataErrorAction({ id: 'engage-first', error })
      ))
    )),
  )});

  public loadLapsedBreakfastCustomers$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(discover.ActionTypes.LOAD_DISCOVER_TILES_SUCCESS),
    map((action: any) => action.payload),
    withLatestFrom(this.store.select(fromRoot.getAutomationsFeatureEnabledMap)),
    filter(([res, featureMap] ) => !featureMap['grow-breakfast'] && this.ds.findById('grow-breakfast', res)),
    switchMap(() => this.ds.loadLapsedBreakfastCustomers().pipe(
      map((res) => new discover.LoadTileDataSuccessAction(res)),
      catchError((error) => of(
        new discover.LoadTileDataErrorAction({ id: 'grow-breakfast', error })
      ))
    )),
  )});

  public loadAutomationAudience$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(discover.ActionTypes.LOAD_DISCOVER_TILES_SUCCESS),
    map((action: any) => action.payload),
    withLatestFrom(this.store.select(fromRoot.getAutomationsFeatureEnabledMap)),
    // A new tile load replaces in-flight audience calls. Retry backoff can
    // otherwise finish later and write the previous location onto this tile.
    switchMap(([payload, featureMap]) =>
      of(...payload).pipe(
        filter((tile: any) => featureMap[tile.id] && this.ds.findById(tile.id, payload)),
        mergeMap((tile) => this.loadAutomationAudience(tile.id, false))
      )
    ),
  )});

  // The audience service can still return the pre-send customer list for a while.
  // Remember who was just sent to, drop them from the count immediately, and
  // refetch once the user is back on Discover.
  public adjustAudienceAfterCampaign$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(
      engagement.ActionTypes.SEND_REWARD_SUCCESS,
      engagement.ActionTypes.SEND_EMAIL_SUCCESS,
      automation.ActionTypes.CREATE_AUTOMATION_COMPLETE
    ),
    mergeMap((action) => this.audienceRefreshContext(action)),
    filter((context) => context.refresh),
    mergeMap((context) => this.adjustAudienceAfterSend(context))
  )});

  public refreshStaleAudiences$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(discover.ActionTypes.REFRESH_STALE_AUDIENCES),
    mergeMap(() => from(this.ds.takeStaleAudienceTileIds())),
    map((id) => new discover.LoadTileDataAction({ id }))
  )});

  public retryAutomationAudience$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(discover.ActionTypes.LOAD_TILE_DATA),
    map((action: discover.LoadTileDataAction) => action.payload),
    filter((payload: any) => !!payload?.id && !!DiscoverConstants.tileToAutomationIdsMapping[payload.id]),
    mergeMap((payload) => this.loadAutomationAudience(payload.id, true).pipe(
      takeUntil(this.actions$.pipe(ofType(discover.ActionTypes.LOAD_DISCOVER_TILES_SUCCESS)))
    ))
  )});

  public loadGrowDriveThruCustomers$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(discover.ActionTypes.LOAD_DISCOVER_TILES_SUCCESS),
    map((action: any) => action.payload),
    withLatestFrom(this.store.select(fromRoot.getAutomationsFeatureEnabledMap)),
    filter(([res, featureMap]) => !featureMap['grow-drive-thru-destination'] && this.ds.findById('grow-drive-thru-destination', res)),
    switchMap(() => this.ds.loadGrowDriveThruCustomers().pipe(
      map((res) => new discover.LoadTileDataSuccessAction(res)),
      catchError((error) => of(
        new discover.LoadTileDataErrorAction({ id: 'grow-drive-thru-destination', error })
      ))
    )),
  )});

  public loadGrowCurbsideCustomers$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(discover.ActionTypes.LOAD_DISCOVER_TILES_SUCCESS),
    map((action: any) => action.payload),
    withLatestFrom(this.store.select(fromRoot.getAutomationsFeatureEnabledMap)),
    filter(([res, featureMap]) => !featureMap['grow-curbside-destination'] && this.ds.findById('grow-curbside-destination', res)),
    switchMap(() => this.ds.loadGrowCurbsideCustomers().pipe(
      map((res) => new discover.LoadTileDataSuccessAction(res)),
      catchError((error) => of(
        new discover.LoadTileDataErrorAction({ id: 'grow-curbside-destination', error })
      ))
    )),
  )});

  public loadChurnCustomers$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(discover.ActionTypes.LOAD_DISCOVER_TILES_SUCCESS),
    map((action: any) => action.payload),
    withLatestFrom(this.store.select(fromRoot.getAutomationsFeatureEnabledMap)),
    filter(([res, featureMap]) => !featureMap['churn-flag'] && this.ds.findById('churn-flag', res)),
    switchMap(() => this.ds.loadChurnCustomers().pipe(
      map((res) => new discover.LoadTileDataSuccessAction(res)),
      catchError((error) => of(
        new discover.LoadTileDataErrorAction({ id: 'churn-flag', error })
      ))
    )),
  )});

  public loadMaximizeRedemption$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(discover.ActionTypes.LOAD_DISCOVER_TILES_SUCCESS),
    map((action: any) => action.payload),
    withLatestFrom(this.store.select(fromRoot.getAutomationsFeatureEnabledMap)),
    filter(([res, featureMap]) => !featureMap['maximize-redemption'] && this.ds.findById('maximize-redemption', res)),
    switchMap(() => this.ds.loadMaximizeRedemption().pipe(
      map((res) => new discover.LoadTileDataSuccessAction(res)),
      catchError((error) => of(
        new discover.LoadTileDataErrorAction({ id: 'maximize-redemption', error })
      ))
    )),
  )});

  public loadOldReward$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(discover.ActionTypes.LOAD_DISCOVER_TILES_SUCCESS),
    map((action: any) => action.payload),
    withLatestFrom(this.store.select(fromRoot.getAutomationsFeatureEnabledMap)),
    filter(([res, featureMap]) => !featureMap['operator-led-delivery'] && this.ds.findById('operator-led-delivery', res)),
    switchMap(() => this.ds.loadOldReward().pipe(
      map((res) => new discover.LoadTileDataSuccessAction(res)),
      catchError((error) => of(
        new discover.LoadTileDataErrorAction({ id: 'operator-led-delivery', error })
      ))
    )),
  )});

  public loadFilters$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(discover.ActionTypes.LOAD_DISCOVER_TILES_SUCCESS),
    map((action: any) => action.payload),
    withLatestFrom(this.store.select(fromRoot.getAutomationsFeatureEnabledMap)),
    switchMap(([tiles, featureMap]) => this.ds.loadFilters(tiles).pipe(
      mergeMap((filters ) => {
        let actionArr = [];
        if (filters['recognize-catering'] && !featureMap['recognize-catering']) {
          actionArr.push(
            new discover.LoadTileDataSuccessAction({
              id: 'recognize-catering',
              ...filters['online'][0]
            }));
        }
        if (filters['cfa-one-filters']) {
          actionArr.push(
            new discover.LoadTileDataSuccessAction({
              id: 'cfa-one-filters',
              filters: filters['cfa']
            }));
        }
        return actionArr;
      }),
      catchError((error) => of(new discover.LoadTileFiltersErrorAction(error)))
    )),
  )});

  public loadCampaignErrorsTile$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(discover.ActionTypes.LOAD_DISCOVER_TILES_SUCCESS),
    map((action: any) => action.payload),
    map((res) => this.ds.getTileById('error-tile', res)),
    map((res) => new discover.LoadTileDataSuccessAction(res)),
    catchError((error) => of(
      new discover.LoadTileDataErrorAction({ id: 'error-tile', error })
    ))
  )});

  public markResolvedFailedCampaign$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(discover.ActionTypes.FAILED_CAMPAIGN_MARK_RESOLVED),
    map((action: any) => action.payload),
    switchMap((req) => this.ds.markResolvedFailedCampaign(req).pipe(
      map((res) => new discover.FailedCampaignMarkResolvedSuccess(res)),
      catchError((error) => of(
        new discover.FailedCampaignMarkResolvedFailure({ id: 'error-tile', error }),
      ))
    ))
  )});

  constructor(
    private actions$: Actions,
    private ds: DiscoverService,
    private store: Store<fromRoot.State>
  ) { }

  private audienceRefreshContext(action: Action): Observable<{ refresh: boolean; tileId?: string; customerIds: string[] }> {
    if (action.type === engagement.ActionTypes.SEND_REWARD_SUCCESS
      || action.type === engagement.ActionTypes.SEND_EMAIL_SUCCESS) {
      return this.store.select(fromRoot.getCurrentEngagementId).pipe(
        take(1),
        concatLatestFrom(() => this.store.select((state: any) =>
          state?.engagement?.offerRecipients ?? state?.engagement?.selectedCustomers ?? [])),
        map(([flowId, customerIds]) => ({ refresh: true, tileId: flowId, customerIds }))
      );
    }

    const payload = (action as automation.CreateAutomationComplete).payload;
    const isOneTimeRewardOrEmail = !payload?.error
      && payload?.automated === false
      && (payload?.campaignType === CampaignType.Reward || payload?.campaignType === CampaignType.Notification);
    if (!isOneTimeRewardOrEmail) {
      return of({ refresh: false, customerIds: [] });
    }

    return this.store.select(fromRoot.getSelectedAutomation).pipe(
      take(1),
      map((selected) => ({
        refresh: true,
        tileId: this.tileIdFromAutomation(selected?.templateID),
        customerIds: selected?.customerIDs ?? []
      }))
    );
  }

  private tileIdFromAutomation(templateOrTileId?: string): string | undefined {
    if (!templateOrTileId) {
      return undefined;
    }
    return DiscoverConstants.automationToTileIdsMapping[templateOrTileId]
      ?? (DiscoverConstants.tileToAutomationIdsMapping[templateOrTileId] ? templateOrTileId : undefined);
  }

  private adjustAudienceAfterSend(context: { tileId?: string; customerIds: string[] }): Observable<Action> {
    return this.store.select(fromRoot.getAutomationsFeatureEnabledMap).pipe(
      take(1),
      concatLatestFrom(() => this.store.select(fromRoot.getDiscoverTiles)),
      switchMap(([featureMap, tiles]) => {
        if (!context.tileId) {
          return from([]);
        }
        const loadedIds = new Set((tiles ?? []).map((tile) => tile.id));
        const targets = [context.tileId].filter((id) =>
          loadedIds.has(id) && featureMap?.[id] && DiscoverConstants.tileToAutomationIdsMapping[id]
        );
        const updates: Action[] = [];
        targets.forEach((tileId) => {
          const templateId = DiscoverConstants.tileToAutomationIdsMapping[tileId];
          const tileCustomers = (tiles ?? []).find((tile) => tile.id === tileId)?.customers;
          const customerIds = context.customerIds?.length ? context.customerIds : tileCustomers;
          const updated = this.ds.recordSentCustomers(templateId, customerIds ?? []);
          if (updated) {
            updates.push(new discover.LoadTileDataSuccessAction(updated));
          }
          this.ds.clearAutomationAudienceCache(templateId);
        });
        this.ds.markAudiencesStale(targets);
        return from(updates);
      })
    );
  }

  private loadAutomationAudience(tileId: string, bypassCache: boolean) {
    return this.ds.loadAutomationAudienceByTemplateId(
      DiscoverConstants.tileToAutomationIdsMapping[tileId],
      bypassCache
    ).pipe(
      map((res) => new discover.LoadTileDataSuccessAction(res)),
      catchError((error) => of(
        new discover.LoadTileDataErrorAction({ id: tileId, error, errorText: error })
      ))
    );
  }
}
